import { kickCommand, statusQuery, decodeStatus } from "./escpos";
import {
  DrawerTransportError,
  type ConnectionState,
  type DrawerTransport,
  type PeripheralDevice,
} from "./transport";

/**
 * WebUSB transport for the drawer (T132, contracts/peripheral-drawer.md).
 *
 * WEBUSB IS THE PRIMARY TRANSPORT, and that is a platform fact rather than a
 * preference: terminals are Chrome on Android, where WebHID does not exist and
 * no browser on iOS exposes either API (research.md R15). An iPad cannot open
 * a cash drawer by any means, which is why the hardware requirement decides the
 * device rather than the other way round.
 *
 * `webbluetooth-transport.ts` is the alternative, for printers that expose a
 * BLE serial service. Prefer this one where a cable is an option: the USB grant
 * reconnects silently across reloads, where the Bluetooth grant may not.
 *
 * The printer is used SOLELY as a solenoid driver. Nothing is printed here. The
 * same transport is what a receipt-printing module would use later, which is
 * why FR-108 puts both behind one output boundary.
 *
 * NO SERVER ROUND TRIP IS ON THIS PATH. A drawer must release during an outage
 * (FR-101), so this is browser-to-device only; the resulting event is queued
 * and synced afterwards like any other record.
 */

// The transport-facing types live in `transport.ts` now that there are two
// transports. Re-exported here so existing importers keep working.
export {
  DrawerTransportError,
  type ConnectionState,
  type DrawerTransport,
  type FailureCode,
  type PeripheralDevice,
} from "./transport";

/**
 * Printer-class USB interface. `0x07` is the USB printer class, and filtering
 * on it rather than a vendor list is what stops this becoming a hardware
 * compatibility table nobody maintains.
 */
const PRINTER_CLASS = 0x07;

/** A kick that never answers must not hang the till behind the sale. */
const KICK_TIMEOUT_MS = 3_000;

/**
 * Bound for a generic write — a ticket, not a 5-byte kick. Matches
 * SC-202's 15 s budget for a forty-line ticket, the slower case this bounds.
 */
const WRITE_TIMEOUT_MS = 15_000;

/** Conservative chunk size for a single USB transferOut call. */
const USB_WRITE_CHUNK_BYTES = 4_096;

interface UsbLike {
  requestDevice(options: { filters: Array<{ classCode?: number }> }): Promise<UsbDeviceLike>;
  getDevices(): Promise<UsbDeviceLike[]>;
}

interface UsbDeviceLike {
  productName?: string;
  vendorId: number;
  productId: number;
  opened: boolean;
  configuration: { interfaces: UsbInterfaceLike[] } | null;
  open(): Promise<void>;
  close(): Promise<void>;
  selectConfiguration(value: number): Promise<void>;
  claimInterface(value: number): Promise<void>;
  releaseInterface(value: number): Promise<void>;
  transferOut(endpoint: number, data: BufferSource): Promise<{ status: string }>;
  transferIn(endpoint: number, length: number): Promise<{ data?: DataView; status: string }>;
}

interface UsbInterfaceLike {
  interfaceNumber: number;
  alternate: {
    interfaceClass: number;
    endpoints: Array<{ direction: "in" | "out"; endpointNumber: number }>;
  };
}

function usb(): UsbLike | null {
  return (navigator as unknown as { usb?: UsbLike }).usb ?? null;
}

export function isSupported(): boolean {
  return usb() !== null;
}

export class WebUsbDrawerTransport implements DrawerTransport {
  #device: UsbDeviceLike | null = null;
  #interfaceNumber: number | null = null;
  #outEndpoint: number | null = null;
  #inEndpoint: number | null = null;

  isSupported(): boolean {
    return isSupported();
  }

  /**
   * Prompt for a device. REQUIRES A USER GESTURE — Chrome will reject it
   * otherwise, which is why this is only ever reached from a button in the
   * peripherals settings screen and never from a sale.
   */
  async requestPermission(): Promise<PeripheralDevice> {
    const bridge = usb();
    if (!bridge) {
      throw new DrawerTransportError(
        "unsupported_browser",
        "This browser cannot talk to USB devices.",
      );
    }

    const device = await bridge.requestDevice({ filters: [{ classCode: PRINTER_CLASS }] });
    return {
      vendorId: device.vendorId,
      productId: device.productId,
      name: device.productName ?? "USB printer",
    };
  }

  /**
   * Open a previously granted device.
   *
   * A grant survives a reload but NOT a browser reinstall or a revoked
   * permission, so an empty device list is reported as `permission_lost` rather
   * than treated as a hardware fault — the two need different recovery, and
   * telling staff to check a cable when the grant is gone wastes their time
   * (FR-105).
   */
  async connect(): Promise<ConnectionState> {
    const bridge = usb();
    if (!bridge) return "disconnected";

    const granted = await bridge.getDevices();
    const device = granted[0];
    if (!device) return "permission_lost";

    try {
      if (!device.opened) await device.open();
      if (!device.configuration) await device.selectConfiguration(1);

      const printerInterface = device.configuration?.interfaces.find(
        (candidate) => candidate.alternate.interfaceClass === PRINTER_CLASS,
      );
      if (!printerInterface) return "disconnected";

      await device.claimInterface(printerInterface.interfaceNumber);

      this.#device = device;
      this.#interfaceNumber = printerInterface.interfaceNumber;
      this.#outEndpoint =
        printerInterface.alternate.endpoints.find((e) => e.direction === "out")?.endpointNumber ??
        null;
      this.#inEndpoint =
        printerInterface.alternate.endpoints.find((e) => e.direction === "in")?.endpointNumber ??
        null;

      return this.#outEndpoint === null ? "disconnected" : "connected";
    } catch {
      return "disconnected";
    }
  }

  isConnected(): boolean {
    return this.#device !== null && this.#outEndpoint !== null;
  }

  /** Whether this device can report drawer state at all (FR-106). */
  hasDrawerStatus(): boolean {
    return this.#inEndpoint !== null;
  }

  /**
   * Pulse the kick-out connector.
   *
   * Bounded by a timeout, because a half-dead printer that accepts the write
   * and never completes it would otherwise leave the staff member staring at a
   * spinner with the customer's money already in the drawer.
   */
  async kick(pin: 2 | 5 = 2): Promise<void> {
    if (!this.#device || this.#outEndpoint === null) {
      throw new DrawerTransportError("device_disconnected", "No drawer device is connected.");
    }

    const write = this.#device.transferOut(this.#outEndpoint, kickCommand(pin) as BufferSource);

    const result = await Promise.race([
      write,
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new DrawerTransportError("kick_timeout", "The drawer did not respond.")),
          KICK_TIMEOUT_MS,
        ),
      ),
    ]);

    if (result.status !== "ok") {
      throw new DrawerTransportError("device_disconnected", `Transfer returned ${result.status}`);
    }
  }

  /**
   * Send arbitrary bytes — a ticket. Chunked because a forty-line ticket can
   * exceed what a single `transferOut` call reliably carries; bounded by
   * `WRITE_TIMEOUT_MS` rather than the kick's tighter timeout, because this
   * is thousands of bytes, not five.
   */
  async write(bytes: Uint8Array): Promise<void> {
    const device = this.#device;
    const outEndpoint = this.#outEndpoint;
    if (!device || outEndpoint === null) {
      throw new DrawerTransportError("device_disconnected", "No drawer device is connected.");
    }

    const send = async () => {
      for (let offset = 0; offset < bytes.length; offset += USB_WRITE_CHUNK_BYTES) {
        const chunk = bytes.slice(offset, offset + USB_WRITE_CHUNK_BYTES);
        const result = await device.transferOut(outEndpoint, chunk as BufferSource);
        if (result.status !== "ok") {
          throw new DrawerTransportError("device_disconnected", `Transfer returned ${result.status}`);
        }
      }
    };

    await Promise.race([
      send(),
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new DrawerTransportError("kick_timeout", "The printer did not respond.")),
          WRITE_TIMEOUT_MS,
        ),
      ),
    ]);
  }

  /**
   * Read drawer state, ONLY where the device supports it.
   *
   * Returns null rather than a guess when unsupported. A wrong "drawer left
   * open" alert is worse than no alert, because staff learn to ignore it and
   * then ignore the real one (FR-106).
   */
  async readDrawerState(): Promise<"open" | "closed" | null> {
    if (!this.#device || this.#outEndpoint === null || this.#inEndpoint === null) return null;

    try {
      await this.#device.transferOut(this.#outEndpoint, statusQuery() as BufferSource);
      const response = await this.#device.transferIn(this.#inEndpoint, 1);
      const byte = response.data?.getUint8(0);
      if (byte === undefined) return null;
      return decodeStatus(byte).drawerOpen ? "open" : "closed";
    } catch {
      return null;
    }
  }

  async disconnect(): Promise<void> {
    const device = this.#device;
    const claimed = this.#interfaceNumber;

    this.#device = null;
    this.#interfaceNumber = null;
    this.#outEndpoint = null;
    this.#inEndpoint = null;

    if (!device) return;
    try {
      if (claimed !== null) await device.releaseInterface(claimed);
      await device.close();
    } catch {
      // Already gone. Nothing to release, and nothing worth telling staff.
    }
  }
}
